// React
import { memo, useCallback, useState } from "react";

// MUI
import { Grid, Skeleton } from "@mui/material";

// Components
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import SearchInput from "@Layouts/components/Basics/SearchInput";
import TimeAndPayRatesEmptyBody from "@/DataPages/layouts/TimeAndPayRates/TimeAndPayRatesEmptyBody";
import Department from "@/DataPages/layouts/TimeAndPayRates/Department";

// Types
import { Nullable } from "@/Common/interfaces/types";
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// Images
import { IDepartment } from "@/DataPages/types/timeAndPayRates/models";

// Helpers
import { debounceFn } from "@/Common/helpers/functions";

// Constants
import { DEBOUNCE_DELAY_500 } from "@/Constants/debounce";

type TSectionTimeCardDepartmentsProps = {
	selected: IPeriodTreeSelection;
	timeCards: Nullable<IDepartment[]>;
	isLoading: boolean;
	searchValue: string;
};

const SectionTimeCardDepartments = memo((props: Readonly<TSectionTimeCardDepartmentsProps>) => {
	const { selected, timeCards, isLoading, searchValue } = props;

	return (
		<Grid className="mb-40px">
			<Grid item xs={12}>
				{isLoading ? (
					<Grid item mt={2} xs={12} mb={5}>
						<Skeleton variant="rectangular" width={280} height={25} sx={{ borderRadius: "6px" }} />
						<Skeleton variant="rectangular" width={100} height={20} sx={{ borderRadius: "6px", marginTop: "20px" }} />
						<TableLoader content rows={7} />
					</Grid>
				) : (
					<>
						{timeCards?.length ? (
							<Grid container item mt={0} rowSpacing={2}>
								{timeCards.map((item) => {
									return (
										<Department
											key={item.department.replace(" ", "")}
											content={item}
											searchValue={searchValue}
											renderOption="timecard"
										/>
									);
								})}
							</Grid>
						) : (
							<TimeAndPayRatesEmptyBody selected={selected} />
						)}
					</>
				)}
			</Grid>
		</Grid>
	);
});

interface ITimeCardsContentProps {
	selected: IPeriodTreeSelection;
	timeCards: Nullable<IDepartment[]>;
	isLoading: boolean;
}

const TimeCardsContent = (props: ITimeCardsContentProps) => {
	const { selected, isLoading, timeCards } = props;
	const [searchValue, setSearchValue] = useState<string>("");
	const [globalSearch, setGlobalSearch] = useState<string>("");

	const memoFn = useCallback(
		debounceFn((p: string) => setGlobalSearch(p), DEBOUNCE_DELAY_500),
		[]
	);

	const handleSearch = (value: string) => {
		setSearchValue(value);
		memoFn(value);
	};

	const shouldBeDisplayFlex = isLoading || timeCards?.length ? "flex" : "none";

	return (
		<>
			<Grid container mt={1} justifyContent={"flex-end"} display={{ display: shouldBeDisplayFlex }}>
				<Grid item xs={3}>
					<SearchInput
						value={searchValue}
						loading={isLoading}
						onChange={handleSearch}
						clearSearch={() => {
							setSearchValue("");
							setGlobalSearch("");
						}}
						sx={{ display: shouldBeDisplayFlex }}
					/>
				</Grid>
			</Grid>
			<SectionTimeCardDepartments isLoading={isLoading} searchValue={globalSearch} selected={selected} timeCards={timeCards} />
		</>
	);
};

export default TimeCardsContent;
